import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import "./RichTextEditor.css";

function ToolbarButton({
	onClick,
	active = false,
	disabled = false,
	icon,
	title,
}) {
	return (
		<button
			type="button"
			className={`rte-btn ${active ? "is-active" : ""}`}
			onClick={onClick}
			disabled={disabled}
			title={title}
		>
			<i className={icon} aria-hidden="true" />
		</button>
	);
}

export default function RichTextEditor({
	value,
	onChange,
	placeholder = "Escribe aquí...",
	error = false,
}) {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: { levels: [1, 2, 3] },
				link: false,
			}),
			Link.configure({
				autolink: true,
				openOnClick: false,
				HTMLAttributes: {
					rel: "noopener noreferrer nofollow",
					target: "_blank",
				},
			}),
			Placeholder.configure({
				placeholder,
			}),
		],
		content: value || "",
		onUpdate: ({ editor: currentEditor }) => {
			onChange(currentEditor.getHTML());
		},
	});

	useEffect(() => {
		if (!editor) return;
		const current = editor.getHTML();
		const incoming = value || "";
		if (current !== incoming) {
			editor.commands.setContent(incoming, { emitUpdate: false });
		}
	}, [editor, value]);

	if (!editor) return null;

	return (
		<div className={`rte-wrapper ${error ? "is-invalid" : ""}`}>
			<div className="rte-toolbar" role="toolbar" aria-label="Editor">
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleBold().run()}
					active={editor.isActive("bold")}
					icon="ri-bold"
					title="Negrita"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleItalic().run()}
					active={editor.isActive("italic")}
					icon="ri-italic"
					title="Cursiva"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().toggleStrike().run()}
					active={editor.isActive("strike")}
					icon="ri-strikethrough"
					title="Tachado"
				/>
				<div className="rte-divider" />
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleHeading({ level: 2 }).run()
					}
					active={editor.isActive("heading", { level: 2 })}
					icon="ri-h-2"
					title="Título"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleBulletList().run()
					}
					active={editor.isActive("bulletList")}
					icon="ri-list-unordered"
					title="Lista"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleOrderedList().run()
					}
					active={editor.isActive("orderedList")}
					icon="ri-list-ordered"
					title="Lista numerada"
				/>
				<div className="rte-divider" />
				<ToolbarButton
					onClick={() => {
						const previousUrl =
							editor.getAttributes("link").href || "";
						const url = window.prompt(
							"Ingresa la URL del enlace",
							previousUrl,
						);

						if (url === null) return;
						if (!url.trim()) {
							editor.chain().focus().unsetLink().run();
							return;
						}

						editor
							.chain()
							.focus()
							.extendMarkRange("link")
							.setLink({ href: url.trim() })
							.run();
					}}
					active={editor.isActive("link")}
					icon="ri-link"
					title="Enlace"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleBlockquote().run()
					}
					active={editor.isActive("blockquote")}
					icon="ri-double-quotes-l"
					title="Cita"
				/>
				<ToolbarButton
					onClick={() =>
						editor.chain().focus().toggleCodeBlock().run()
					}
					active={editor.isActive("codeBlock")}
					icon="ri-code-box-line"
					title="Bloque de código"
				/>
				<div className="rte-divider" />
				<ToolbarButton
					onClick={() => editor.chain().focus().undo().run()}
					disabled={!editor.can().chain().focus().undo().run()}
					icon="ri-arrow-go-back-line"
					title="Deshacer"
				/>
				<ToolbarButton
					onClick={() => editor.chain().focus().redo().run()}
					disabled={!editor.can().chain().focus().redo().run()}
					icon="ri-arrow-go-forward-line"
					title="Rehacer"
				/>
			</div>

			<EditorContent editor={editor} className="rte-content" />
		</div>
	);
}
